import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * FormDialog component props
 * Generic form dialog that follows the same pattern as ConfirmationDialog and InfoDialog
 * but accepts custom form content as children
 */
interface FormDialogProps {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  children: React.ReactNode;
}

/**
 * FormDialog component
 * Reusable form dialog for custom form content
 * Follows the same pattern as the existing project dialogs (ConfirmationDialog, InfoDialog)
 */
const FormDialog: React.FC<FormDialogProps> = ({
  title,
  onConfirm,
  onCancel,
  confirmText = 'Save',
  cancelText = 'Cancel',
  children
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
    // Don't auto-submit on Enter as the form might have text inputs
  };

  /**
   * Focus management on mount
   */
  useEffect(() => {
    if (confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, []);

  /**
   * Prevent background scrolling when dialog is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const dialogContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000
      }}
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-labelledby="dialog-title"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '2rem',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Dialog Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h2
            id="dialog-title"
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: '#333'
            }}
          >
            {title}
          </h2>
        </div>

        {/* Dialog Content (Form) */}
        <div style={{ marginBottom: '1.5rem' }}>
          {children}
        </div>

        {/* Dialog Actions */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#e91e8c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // Render in a portal so the overlay escapes parent containers
  // and covers the full viewport
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(dialogContent, document.body);
  }

  // Fallback for non-DOM environments
  return dialogContent;
};

export default FormDialog;

